import React, { useEffect, useState } from 'react';
import api from '../../utils/api';
import { Plus, Search } from 'lucide-react';

const DowntimeEntry = () => {
  const [entries, setEntries] = useState([]);
  const [machines, setMachines] = useState([]);
  const [reasons, setReasons] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const emptyForm = { machine_id: '', reason_id: '', shift_id: '', start_time: '', end_time: '', duration_minutes: 0, notes: '' };
  const [formData, setFormData] = useState({ ...emptyForm });

  useEffect(() => { fetchData(); }, []);
  const fetchData = async () => {
    try {
      const [eRes, mRes, rRes, sRes] = await Promise.all([
        api.get('/production/downtime-entries'), api.get('/production/machines'),
        api.get('/production/downtime-reasons'), api.get('/sales/shifts')
      ]);
      setEntries(eRes.data); setMachines(mRes.data); setReasons(rRes.data); setShifts(sRes.data);
    } catch(e) { console.error(e); } finally { setLoading(false); }
  };

  const openCreate = () => { setFormData({ ...emptyForm }); setShowModal(true); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/production/downtime-entries', formData);
      setShowModal(false); fetchData();
    } catch (err) { alert(err.response?.data?.detail || 'Failed'); }
  };

  const filtered = entries.filter(e => (e.machine_name || '').toLowerCase().includes(searchTerm.toLowerCase()) || (e.reason || '').toLowerCase().includes(searchTerm.toLowerCase()));

  const totalDowntimeMin = entries.reduce((s, e) => s + (e.duration_minutes || 0), 0);

  return (
    <div data-testid="downtime-entry-page">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 style={{ fontSize: '24px', fontWeight: 700, margin: 0 }}>Downtime Entry</h1>
          <p style={{ margin: '4px 0 0', color: 'var(--text-tertiary)', fontSize: '13px' }}>Execution Layer - Log machine downtime events</p>
        </div>
        <button onClick={openCreate} className="btn btn-primary" data-testid="add-downtime-btn"><Plus size={16} /> Log Downtime</button>
      </div>

      <div className="grid grid-cols-3 mb-4">
        <div className="stat-card" style={{ borderLeft: '4px solid #DC2626' }} data-testid="stat-total-events">
          <div className="stat-value">{entries.length}</div>
          <div className="stat-label">Total Events</div>
        </div>
        <div className="stat-card" style={{ borderLeft: '4px solid #D97706' }} data-testid="stat-total-minutes">
          <div className="stat-value">{totalDowntimeMin}</div>
          <div className="stat-label">Total Minutes</div>
        </div>
        <div className="stat-card" style={{ borderLeft: '4px solid #6366F1' }} data-testid="stat-total-hours">
          <div className="stat-value">{(totalDowntimeMin / 60).toFixed(1)}</div>
          <div className="stat-label">Total Hours</div>
        </div>
      </div>

      <div className="card mb-4">
        <div style={{ position: 'relative' }}>
          <Search size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-tertiary)' }} />
          <input type="text" className="form-input" placeholder="Search by machine or reason..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} style={{ paddingLeft: '40px' }} data-testid="search-input" />
        </div>
      </div>

      <div className="card">
        {loading ? <div>Loading...</div> : (
          <div className="table-container">
            <table>
              <thead><tr><th>Machine</th><th>Reason</th><th>Start Time</th><th>End Time</th><th>Duration (min)</th><th>Notes</th><th>Logged At</th></tr></thead>
              <tbody>
                {filtered.length === 0 ? <tr><td colSpan={7} style={{ textAlign: 'center', padding: '20px', color: 'var(--text-tertiary)' }}>No downtime entries</td></tr> : filtered.map(e => (
                  <tr key={e.id} data-testid={`downtime-row-${e.id}`}>
                    <td style={{ fontWeight: 500 }}>{e.machine_name || '-'}</td>
                    <td>{e.reason || '-'}</td>
                    <td className="font-mono text-xs">{e.start_time || '-'}</td>
                    <td className="font-mono text-xs">{e.end_time || '-'}</td>
                    <td className="tabular-nums" style={{ fontWeight: 600, color: '#DC2626' }}>{e.duration_minutes || 0}</td>
                    <td>{e.notes || '-'}</td>
                    <td className="font-mono text-xs">{e.created_at ? new Date(e.created_at).toLocaleString() : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showModal && (
        <div className="modal-overlay" data-testid="downtime-modal">
          <div className="modal-content" style={{ maxWidth: '550px' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header"><h2 style={{ margin: 0, fontSize: '18px', fontWeight: 700 }}>Log Downtime</h2></div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="grid grid-cols-2">
                  <div className="form-group">
                    <label className="form-label">Machine *</label>
                    <select className="form-select" value={formData.machine_id} onChange={(e) => setFormData({...formData, machine_id: e.target.value})} required data-testid="machine-select">
                      <option value="">Select Machine</option>
                      {machines.map(m => <option key={m.id} value={m.id}>{m.name} ({m.machine_code})</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Reason *</label>
                    <select className="form-select" value={formData.reason_id} onChange={(e) => setFormData({...formData, reason_id: e.target.value})} required data-testid="reason-select">
                      <option value="">Select Reason</option>
                      {reasons.map(r => <option key={r.id} value={r.id}>{r.reason} ({r.category})</option>)}
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-2">
                  <div className="form-group"><label className="form-label">Start Time</label><input type="datetime-local" className="form-input" value={formData.start_time} onChange={(e) => setFormData({...formData, start_time: e.target.value})} data-testid="start-time-input" /></div>
                  <div className="form-group"><label className="form-label">End Time</label><input type="datetime-local" className="form-input" value={formData.end_time} onChange={(e) => setFormData({...formData, end_time: e.target.value})} data-testid="end-time-input" /></div>
                </div>
                <div className="grid grid-cols-2">
                  <div className="form-group"><label className="form-label">Duration (minutes) *</label><input type="number" className="form-input" value={formData.duration_minutes} onChange={(e) => setFormData({...formData, duration_minutes: parseInt(e.target.value) || 0})} required min={1} data-testid="duration-input" /></div>
                  <div className="form-group">
                    <label className="form-label">Shift</label>
                    <select className="form-select" value={formData.shift_id} onChange={(e) => setFormData({...formData, shift_id: e.target.value})} data-testid="shift-select">
                      <option value="">Select Shift</option>
                      {shifts.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                  </div>
                </div>
                <div className="form-group"><label className="form-label">Notes</label><textarea className="form-textarea" value={formData.notes} onChange={(e) => setFormData({...formData, notes: e.target.value})} rows={2} data-testid="notes-input" /></div>
              </div>
              <div className="modal-footer">
                <button type="button" onClick={() => setShowModal(false)} className="btn btn-secondary">Cancel</button>
                <button type="submit" className="btn btn-primary" data-testid="submit-downtime-btn">Log Downtime</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default DowntimeEntry;
